import { Check, ChevronDown, Clock3, ListFilter, Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const TASK_FILTER_OPTIONS = [
  {
    value: "DEFAULT",
    label: "Default",
    description: "Balanced task order",
    Icon: ListFilter,
  },
  {
    value: "NEW_TASK",
    label: "New Task",
    description: "Latest tasks first",
    Icon: Sparkles,
  },
  {
    value: "NEW_SUBTASK",
    label: "Newly added subtask",
    description: "Recent subtask updates",
    Icon: Sparkles,
  },
  {
    value: "DEADLINES",
    label: "Deadlines nearer",
    description: "Closest due dates first",
    Icon: Clock3,
  },
];

export default function TaskFilterDropdown({
  value,
  onChange,
  variant = "dark",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  const selectedOption = useMemo(
    () =>
      TASK_FILTER_OPTIONS.find((option) => option.value === value) ||
      TASK_FILTER_OPTIONS[0],
    [value]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateMenuPosition = () => {
    if (!dropdownRef.current) return;

    const rect = dropdownRef.current.getBoundingClientRect();
    const menuWidth = Math.max(rect.width, 270);
    const left = Math.min(
      Math.max(12, rect.right - menuWidth),
      window.innerWidth - menuWidth - 12
    );

    setMenuPosition({
      top: rect.bottom + 8,
      left,
      width: menuWidth,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  const buttonClasses =
    variant === "light"
      ? "border-slate-200 bg-white text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50"
      : "border-white/15 bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] hover:border-cyan-200/45 hover:bg-white/14";

  const menuClasses =
    variant === "light"
      ? "border-slate-200 bg-white text-slate-800 shadow-xl shadow-slate-900/10"
      : "border-cyan-200/20 bg-[#171026] text-white shadow-2xl shadow-black/45";

  return (
    <div ref={dropdownRef} className="relative w-full sm:w-[230px]">
      <button
        type="button"
        onClick={() => {
          updateMenuPosition();
          setIsOpen((current) => !current);
        }}
        className={`flex min-h-10 w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left text-sm transition ${buttonClasses}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-cyan-300/15 text-cyan-200">
            <selectedOption.Icon size={15} />
          </span>
          <span className="min-w-0">
            <span className="block truncate font-semibold">
              {selectedOption.label}
            </span>
            <span className="block truncate text-[11px] opacity-60">
              {selectedOption.description}
            </span>
          </span>
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 opacity-70 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          className={`fixed z-[9999] overflow-hidden rounded-2xl border p-1.5 ${menuClasses}`}
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
            backgroundColor: variant === "light" ? "#ffffff" : "#0d0818",
          }}
          role="listbox"
        >
          {TASK_FILTER_OPTIONS.map(({ value: optionValue, label, description, Icon }) => {
            const isSelected = optionValue === value;

            return (
              <button
                key={optionValue}
                type="button"
                onClick={() => {
                  onChange(optionValue);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                  isSelected
                    ? "bg-cyan-300/14 text-cyan-100"
                    : "hover:bg-white/10"
                }`}
                role="option"
                aria-selected={isSelected}
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-white/10">
                  <Icon size={15} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">
                    {label}
                  </span>
                  <span className="block truncate text-[11px] opacity-60">
                    {description}
                  </span>
                </span>
                {isSelected && <Check size={16} className="text-cyan-200" />}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
